import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TicketsStatistics from "./tickets-statistics";
import Payments from "./payments";
import PaymentMethod from "./payment-method";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useGetLocationsList } from "src/api/locations";
import { useGetClientsList } from "src/api/clients";
import { IClientResponse } from "src/types/clients";
import { LoadingButton } from "@mui/lab";
import { useGetBISummary } from "src/api/business-intelligence";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import { ICity } from "src/types/generic";
import { useGetCitiesList } from "src/api/general";
import { DatePicker } from "@mui/x-date-pickers";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import Analytics from "./analytics";
import StayTimeBreakdown from "./stay-time-breakdown";
import WaitingTimeBreakdown from "./waiting-time-breakdown";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const initStartDate = moment().startOf("year").toDate();
const initEndDate = moment().toDate();

const minDate = new Date("2024-01-01T00:00:00");
const maxDate = moment().add(1, "year").endOf("year").toDate();

const BITransactionsPage = () => {
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [tempSelectedLocations, setTempSelectedLocations] = useState<any[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  const [tempSelectedCity, setTempSelectedCity] = useState<ICity>();
  const [selectedClient, setSelectedClient] = useState<IClientResponse>();
  const [tempSelectedClient, setTempSelectedClient] =
    useState<IClientResponse>();
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [selectedCity, setSelectedCity] = useState<ICity>();
  const [startDate, setStartDate] = useState<Date | null>(initStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initEndDate);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(
    initStartDate,
  );
  const [tempEndDate, setTempEndDate] = useState<Date | null>(initEndDate);
  const [selectedLocationType, setSelectedLocationType] =
    useState<string>("all");
  const [tempSelectedLocationType, setTempSelectedLocationType] =
    useState<string>("all");

  const filters = {
    ...(selectedCity && { cities: [selectedCity.id] }),
    ...(selectedClient && { userId: selectedClient.id }),
    ...(selectedLocations.length > 0 && {
      locations: selectedLocations.map((loc) => loc.id),
    }),
    ...(selectedLocationType !== "all" && {
      locationType: selectedLocationType,
    }),
    startDate: moment(startDate).format("YYYY-MM-DD"),
    endDate: moment(endDate).format("YYYY-MM-DD"),
  };

  // APIS
  const { data: locationsData } = useGetLocationsList();
  const { data: citiesData } = useGetCitiesList({ type: "active" });

  const { data: clientsData } = useGetClientsList();

  const { data, isLoading, error, status } = useGetBISummary(filters);

  const summary = data?.data;

  const handleChangeLocations = (value: any[], reason: string) => {
    setTempSelectedLocations(value);
  };

  const handleChangeClient = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedClient(newValue);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedCity(newValue);
  };

  const handleLocationType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempSelectedLocationType(value);
  };

  const handleApply = () => {
    if (
      tempStartDate !== null &&
      tempEndDate !== null &&
      tempStartDate > tempEndDate
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: "Start date must be before end date",
        },
      ]);
      return;
    }

    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSelectedLocations(tempSelectedLocations);
    setSelectedClient(tempSelectedClient);
    setSelectedCity(tempSelectedCity);
    setSelectedLocationType(tempSelectedLocationType);
  };

  const handleResetFilers = () => {
    setEndDate(initEndDate);
    setStartDate(initStartDate);
    setSelectedLocations([]);
    setSelectedClient(undefined);
    setSelectedCity(undefined);
    setSelectedLocationType("all");

    setTempEndDate(initEndDate);
    setTempStartDate(initStartDate);
    setTempSelectedLocations([]);
    setTempSelectedClient(undefined);
    setTempSelectedCity(undefined);
    setTempSelectedLocationType("all");
  };

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  const sortedClients = useMemo(() => {
    return (
      clientsData?.data
        .slice()
        .sort((a, b) =>
          a.clientInfo.nameEn?.localeCompare(b.clientInfo.nameEn),
        ) || []
    );
  }, [clientsData]);

  const filteredLocations = useMemo(() => {
    return tempSelectedClient
      ? sortedLocations.filter(
          (loc: any) => loc.clientId === tempSelectedClient?.id,
        )
      : sortedLocations;
  }, [tempSelectedClient, sortedLocations]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (tempSelectedLocations.length === 0 && !tempSelectedClient) {
      return citiesData.data;
    }

    const availableLocations =
      tempSelectedLocations.length > 0
        ? tempSelectedLocations
        : filteredLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [
    tempSelectedLocations,
    citiesData,
    filteredLocations,
    tempSelectedClient,
  ]);

  useEffect(() => {
    setTempSelectedLocations([]);
    setTempSelectedCity(undefined);
  }, [tempSelectedClient]);

  useEffect(() => {
    setTempSelectedCity(undefined);
  }, [tempSelectedLocations]);

  useEffect(() => {
    if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            error?.response?.data?.message ?? "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  return (
    <Box p={{ xs: "12px", lg: "30px" }}>
      <Typography mb={4} fontSize={"22px"} fontWeight={600}>
        Transactions Report
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={0.5}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          {/* clients */}
          <CustomAutocomplete
            key={tempSelectedClient?.id ?? "client-autocomplete"} // KEY BASED ON VALUE
            sx={{ width: { xs: "100%", sm: "210px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={sortedClients}
            getOptionLabel={(option) => option.clientInfo.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={tempSelectedClient}
            onChange={handleChangeClient}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Client"} />
            )}
          />
          {/* locations */}
          <CustomAutocomplete
            sx={{ width: { xs: "100%", sm: "210px" } }}
            size="small"
            limitTags={1}
            multiple
            id="tags-outlined"
            options={filteredLocations}
            disableCloseOnSelect
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={`${props.id} ${option.id}`}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={selected}
                  size="small"
                  sx={{ padding: "3px 0px", mr: "5px" }}
                />
                <Typography>{option.nameEn}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Location" placeholder="" />
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option: any, index: number) => (
                <Chip
                  size="small"
                  label={option.nameEn}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            value={tempSelectedLocations}
            onChange={(event, value, reason) => {
              handleChangeLocations(value, reason);
            }}
          />
          {/* type */}
          <FormControl size="small" sx={{ width: { xs: "100%", sm: "120px" } }}>
            <InputLabel id="bi-location-type-select-label">
              Location Type
            </InputLabel>
            <Select
              data-testid="filter-location-type-select"
              labelId="bi-location-type-select-label"
              id="bi-location-type-select"
              value={tempSelectedLocationType}
              label="Location Type"
              onChange={handleLocationType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"regular"}>Regular</MenuItem>
              <MenuItem value={"event"}>Event</MenuItem>
            </Select>
          </FormControl>
          {/* city */}
          <CustomAutocomplete
            key={tempSelectedCity?.id ?? "city-autocomplete"}
            sx={{ width: { xs: "100%", sm: "170px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={tempSelectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />

          {/* start date */}
          <DatePicker
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenStartDate(true),
                sx: { width: "112px" },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempStartDate}
            onChange={(newValue) => setTempStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            open={openStartDate}
            onClose={() => setOpenStartDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
          {/* end date */}
          <DatePicker
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenEndDate(true),
                sx: { width: "112px" },
                inputProps: { readOnly: true },
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempEndDate}
            onChange={(newValue) => setTempEndDate(newValue)}
            name="end date"
            disableOpenPicker
            open={openEndDate}
            onClose={() => setOpenEndDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Stack>
        <Stack direction={"row"}>
          <LoadingButton onClick={handleResetFilers}>Clear</LoadingButton>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </LoadingButton>
        </Stack>
      </Stack>
      {isLoading ? (
        <Box display={"flex"} justifyContent={"center"} pt={30}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Analytics data={summary?.analyticsReport} />
          <TicketsStatistics data={summary?.ticketReports} />
          <Box mt={2} sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Payments data={summary?.incomeReports} />
              </Grid>
              <Grid item xs={12} md={6}>
                <PaymentMethod data={summary?.paymentMethodReports} />
              </Grid>
              <Grid item xs={12} md={6}>
                <StayTimeBreakdown
                  data={summary?.stayTimeBreakdown ?? []}
                  unit={summary?.stayTimeUnit ?? ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <WaitingTimeBreakdown
                  data={summary?.waitingTimeBreakdown ?? []}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BITransactionsPage;
